import * as React from "react";
import { InputProps } from "../../interfaces/props/InputProps";
import { Dropdown, IDropdownOption, TooltipHost, Stack, IStackTokens, Spinner, SpinnerSize, FontIcon, ComboBox, IComboBox, IComboBoxOption, IRenderFunction, TooltipOverflowMode, IDropdownStyles } from "@fluentui/react";
import { DatasourceService } from "../../services/DatasourceService";
import { connect } from "react-redux";
import { RootState } from "../../redux";
import { DataSourcePanel } from "./datasourcePanel";
import { DataSourceDropdownInitializer } from "../helpers/datasourceDropdownInitializer";
import { INameValuePairIndex } from "../../interfaces/INameValuePairIndex";
import { DatasourceHelper } from "../helpers/dataSourceHelper";
import { useClientTypeContext } from "../hooks/useClientTypeContext";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { setDatasourceValues } from "../../redux/modules/datasource";
import { IDatasourceQueryDto } from "../../interfaces/IDatasourceQueryDto";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";

const dropdownStyles: Partial<IDropdownStyles> = {
  dropdown: { maxWidth: 301 },
  dropdownOptionText: { overflow: 'visible', whiteSpace: 'normal' },
  dropdownItem: { height: 'auto' },
};

const onRenderTitle = (options: IDropdownOption[], defaultRender?: IRenderFunction<IDropdownOption[]>) => {
  const option: IDropdownOption = Array.isArray(options) && options.length > 0 && options[0];
  return <TooltipHost content={option?.text} overflowMode={TooltipOverflowMode.Parent}>
    {defaultRender(options)}
  </TooltipHost>
}

const getOptions = (allowFreeForm: boolean, defaultOptions: any[], inputValue: string): IDropdownOption[] => {
  const options = Array.isArray(defaultOptions) ? defaultOptions : [];
  if (allowFreeForm && inputValue) {
    if (!options.some(option => option.key && ((option.key as string).trim().toLowerCase() === inputValue.trim().toLowerCase()))) {
      options.push({ key: inputValue, text: inputValue });
    }
  }
  return options;
}

const mapDispatchToProps = {
  setDatasourceValues,
};

const mapStateToProps = (state: RootState) => {
  return {
    datasourceItems: state.tenantInfo.datasources,
    currentDirectory: state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null
  };
};

type reduxDispatch = typeof mapDispatchToProps;
type reduxState = ReturnType<typeof mapStateToProps>;
interface DatasourceDropdownReduxProps extends InputProps, reduxDispatch, reduxState { }

export const UnconnectedDataSourceDropdown: React.FunctionComponent<DatasourceDropdownReduxProps> = ({
  displayName,
  isRequired,
  inputValue,
  inputValidator,
  updateInputValidationStatus,
  error,
  datasourceItems,
  setDatasourceValues,
  currentDirectory,
  containerDisplayNames
}: DatasourceDropdownReduxProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const [isOpen, setIsOpen] = React.useState(false);
  const [datasources, setDatasources] = React.useState<IDropdownOption[]>([]);
  const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
  const [boundedField, setBoundedField] = React.useState<string>("");
  const [boundName] = React.useState<string>(inputValue?.xml?.ContentTypeInputName ?? displayName);
  const dataSourceService = new DatasourceService();
  const dataSourceHelper = new DatasourceHelper(dataSourceService);
  const clientTypes = useClientTypeContext(containerDisplayNames?.siteId ?? currentDirectory?.siteId);
  // useMemo, NOT a one-shot useState initializer: the client-type context can settle after
  // mount, and a captured verdict would silently ignore it. getInputDataSource returns the
  // same object while the verdict is unchanged, so downstream [dataSourceItem] deps are stable.
  const dataSourceItem = React.useMemo(
    () => DatasourceHelper.getInputDataSource(datasourceItems, boundName, [containerDisplayNames?.siteDisplayName ?? currentDirectory?.siteDisplayName ?? ""], [containerDisplayNames?.libraryDisplayName ?? currentDirectory?.DocumentLibrary ?? currentDirectory?.sharepointList ?? ""], clientTypes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientTypes]
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [enableAutoFillRelatedFields, setEnableAutoFillRelatedFields] = React.useState<boolean>(false);
  const isMasterDatasource: boolean = React.useMemo(() => dataSourceItem && boundName && dataSourceItem?.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === boundName.trim().toLowerCase()))?.isMaster,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSourceItem]);
  const allowCustomValues: boolean = React.useMemo(() => dataSourceItem && boundName && dataSourceItem?.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === boundName.trim().toLowerCase()))?.allowCustomValues,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSourceItem]);

  const isFirstRun = React.useRef(true);
  // The datasource the currently shown options were loaded for. `null` = the load effect has
  // not run yet. Lets the effect below distinguish its mount run (keep the "only set when
  // still empty" guard - master-pushed options may already be showing) from a verdict-change
  // re-fire (the shown options belong to the PREVIOUS datasource and MUST be replaced - the
  // old guard silently discarded the reload and kept the excluded datasource's values).
  const optionsLoadedForRef = React.useRef<unknown>(null);
  // Epoch of the newest [dataSourceItem] load run. The re-keying made the load body
  // re-entrant (a verdict settle overlaps the mount run); every write after an await checks
  // it so a SLOW old run resuming after a fast new one cannot stomp the new datasource's
  // state or dispatch the excluded datasource's values.
  const loadEpochRef = React.useRef<number>(0);
  // Bumped whenever a master datasource push narrows the options. A load whose callback
  // lands AFTER a push must not overwrite the narrowed list with the full one - at HEAD the
  // mount-once guard made the push always win; this restores that order-independence.
  const optionsPushEpochRef = React.useRef<number>(0);
  React.useEffect(() => {
    if (!isFirstRun.current) {
      try {
        updateInputValidationStatus(inputValidator.validate(inputValue?.value));
      } catch (e) {
        logger(e, "dataSourceDropdown.tsx (updatedFromDataSourceToggle)");
      }
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  React.useEffect(() => {
    if (!isFirstRun.current) {
      try {
        // Same guards as the load effect's callback: this reload is for the CURRENT
        // datasource - a verdict change (epoch) or a newer master push (pushEpoch) while it
        // is in flight makes its landing stale, and writing it would resurrect an excluded
        // datasource's options or clobber a narrowed list.
        const epoch: number = loadEpochRef.current;
        const pushEpochAtStart: number = optionsPushEpochRef.current;
        const dropdownInitializer = new DataSourceDropdownInitializer(dataSourceService, dataSourceItem, boundName, false);
        dropdownInitializer.initialize((options) => setDatasources(prevOptions =>
          (epoch === loadEpochRef.current && pushEpochAtStart === optionsPushEpochRef.current)
            ? getOptions(allowCustomValues, options, inputValue?.value)
            : prevOptions));
      } catch (e) {
        logger(e, "dataSourceDropdown.tsx (resetDatasourceOptionsToggle)");
      }
    }
  }, [inputValue?.resetDatasourceOptionsToggle]);

  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
    }

    // Mount run: only set when nothing else (a master datasource push) populated the options
    // first. Verdict-change re-fire: the options on screen were loaded for the previous
    // datasource and must be replaced unconditionally.
    const replaceExistingOptions: boolean = optionsLoadedForRef.current !== null && optionsLoadedForRef.current !== dataSourceItem;
    optionsLoadedForRef.current = dataSourceItem;
    const epoch: number = ++loadEpochRef.current;
    const pushEpochAtStart: number = optionsPushEpochRef.current;

    // Excluded verdict (datasource -> undefined): the initializer no-ops on an undefined
    // datasource, so the previous datasource's options must be cleared explicitly here or
    // they stay rendered and selectable.
    if (replaceExistingOptions && !dataSourceItem) {
      setDatasources(getOptions(allowCustomValues, [], inputValue?.value));
    }

    const setupAsyncWrapper = async () => {
      setIsLoading(true);
      try {
        const dropdownInitializer = new DataSourceDropdownInitializer(dataSourceService, dataSourceItem, boundName, false);
        // const [result] = await Promise.all([dataSourceHelper.getSearchStructure(dataSourceItem), dropdownInitializer.initialize(setDatasources)]);
        const [result] = await Promise.all([dataSourceHelper.getSearchStructure(dataSourceItem), dropdownInitializer.initialize((options) => setDatasources(prevOptions =>
          (epoch === loadEpochRef.current
            && pushEpochAtStart === optionsPushEpochRef.current
            && (replaceExistingOptions || prevOptions?.length === 0))
            ? getOptions(allowCustomValues, options, inputValue?.value)
            : prevOptions))]);
        // A newer run superseded this one while it awaited: every remaining write belongs
        // to the new datasource's run (which also owns the loading spinner).
        if (epoch !== loadEpochRef.current) {
          return;
        }
        // Unconditional (not a one-way latch): a verdict-change re-fire must also be able to
        // DISABLE autofill when the new datasource has fewer search fields.
        setEnableAutoFillRelatedFields(Array.isArray(result) && result.length > 1);
        if (Array.isArray(result)) {
          setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
        }
        const boundedFieldName = DatasourceHelper.getBoundedField(dataSourceItem, boundName);
        setBoundedField(boundedFieldName ?? "");

        if (inputValue?.value && boundedFieldName) {
          getRelatedDatasourceValues(inputValue.value, boundedFieldName, false, true);
        }
      } catch (error) {
        logger(error, "dataSourceDropdown.tsx (useEffect 1)");
      }
      if (epoch === loadEpochRef.current) {
        setIsLoading(false);
      }
    };

    setupAsyncWrapper();
    // Keyed on the resolved datasource so a client-type verdict that settles after mount
    // reloads the options instead of being silently ignored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceItem]);

  React.useEffect(() => {
    if (Array.isArray(inputValue?.updatedDatasourceOptions)) {
      try {
        // The narrowed master push wins over any load still in flight (see
        // optionsPushEpochRef) - bump BEFORE writing so a later-landing load callback
        // sees itself outdated and keeps this list.
        optionsPushEpochRef.current++;
        setDatasources(inputValue.updatedDatasourceOptions.map(option => ({ key: option, text: option })));
      } catch (e) {
        logger(e, "dataSourceDropdown.tsx (updatedDatasourceOptions)");
      }
    }
  }, [inputValue?.updatedDatasourceOptions]);

  const closePanel = () => {
    setIsOpen(false);
  };

  const updateInputValue = (newValue: string) => {
    updateInputValidationStatus(inputValidator.validate([newValue]));
    updateSearch(newValue);
  };

  const updateSearchInputs = (index: number, value: string) => {
    DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
  };

  const openPanel = async () => {
    updateSearch(inputValue?.value);
    setIsOpen(true);
  };

  const updateSearch = (newValue: string) => {
    const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
    updateSearchInputs(index, newValue);
  };

  const onChangeHandle = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    updateSearch(item.key as string);
    updateInputValidationStatus(inputValidator.validate(item.key));
    if (enableAutoFillRelatedFields) getRelatedDatasourceValues(item.key as string, boundedField, true);
  };

  const onComboChange = React.useCallback(
    (event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string): void => {
      let key = option?.key;
      const isFreeFormValue: boolean = !option && value?.length > 0;
      if (isFreeFormValue) {
        key = value;
        setDatasources(prevOptions => [...prevOptions, { key: key!, text: value }]);
      }
      updateSearch(key as string);
      updateInputValidationStatus(inputValidator.validate(key));
      if (!isFreeFormValue && enableAutoFillRelatedFields) getRelatedDatasourceValues(key as string, boundedField, true);
    },
    [inputValidator, boundedField, enableAutoFillRelatedFields]
  );

  const getRelatedDatasourceValues = async (searchValue: string, boundedField: string, isStrict: boolean, initialLoad: boolean = false) => {
    if (searchValue?.trim()) {
      try {
        // This function has its OWN await, so the caller's epoch check does not cover it: a
        // datasource change while the Search below is in flight makes this closure's
        // dataSourceItem stale, and dispatching its rows would push an excluded datasource's
        // values into redux (where a push outranks in-flight reloads by design). The redux
        // consumers also drop incompatible pushes via their live-context caller-id check,
        // but the guarantee belongs HERE, at the source.
        const epochAtStart: number = loadEpochRef.current;
        const request: IDatasourceQueryDto = {
          id: dataSourceItem.id,
          name: dataSourceItem.name,
          tenantId: dataSourceItem.tenantId,
          typeId: dataSourceItem.type.id,
          typeName: dataSourceItem.type.name,
          queryParams: [{ name: boundedField, value: searchValue.trim() }],
          returnOnlyQueriedProperties: false,
          isStrict: isStrict,
          allowCache: dataSourceItem.allowCache
        };

        const result = await dataSourceService.Search(request);
        if (epochAtStart !== loadEpochRef.current) {
          return;
        }
        if (result.length === 1) {
          if (!initialLoad) {
            const propertyKeys = Object.keys(result[0]); //keys of the data source, not the sharepoint columns
            if (propertyKeys) {
              let resultItem: any = result[0];
              if (resultItem) {
                let boundObject = {};
                propertyKeys
                  .forEach((key) => {
                    if (resultItem[key] != null) {
                      const boundField = dataSourceItem?.boundFields?.find(field => {
                        return field?.datasourceField.toLowerCase().trim() == key.toLowerCase().trim()
                      }); //check if any of the data source fields have a mapping.
                      if (boundField) { //if a mapping exists
                        const names = boundField.name.split(",").map(name => name.toLowerCase().trim());
                        names
                          .filter(name => name.toLowerCase().trim() !== boundName.toLowerCase().trim())
                          .forEach(name => {
                            boundObject[name] = resultItem[key];
                          })
                      } else {
                        boundObject[key] = resultItem[key];
                      }
                    }
                  });

                setDatasourceValues(boundObject, false, isMasterDatasource ? dataSourceItem?.id : null); //redux.    
              }
            }
          }
        }
        else if (isMasterDatasource && result.length > 1) {
          let resultItem: any = result[0];
          const propertyKeys = resultItem && Object.keys(result[0]);
          if (propertyKeys) {
            const options: any[] = [];
            result.forEach(resultRow => {
              let boundObject = {};
              propertyKeys
                .forEach((key) => {
                  if (resultRow[key]) {
                    const boundField = dataSourceItem?.boundFields?.find(field => {
                      return field?.datasourceField.toLowerCase().trim() == key.toLowerCase().trim()
                    });
                    if (boundField) {
                      const names = boundField.name.split(",").map(name => name.toLowerCase().trim());
                      names
                        .filter(name => name.toLowerCase().trim() !== boundName.toLowerCase().trim())
                        .forEach(name => {
                          boundObject[name] = resultRow[key];
                        })
                    } else {
                      boundObject[key] = resultRow[key];
                    }
                  }
                });
              options.push(boundObject);
            });
            setDatasourceValues(options, initialLoad, dataSourceItem?.id);
          }
        }
      } catch (error) {
        logger(error, "dataSourceDropdown.tsx (getRelatedDatasourceValues)");
      }
    }
  }

  const input = allowCustomValues
    ?
    <ComboBox
      allowFreeform
      useComboBoxAsMenuWidth
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={inputValue ? inputValue?.value : undefined}
      label={displayName}
      placeholder={languageStrings.SelectOption}
      options={datasources}
      onChange={onComboChange}
      required={isRequired()}
      errorMessage={error}
    />
    :
    <Dropdown
      label={displayName}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={inputValue ? inputValue?.value : undefined}
      placeholder={languageStrings.SelectOption}
      onChange={onChangeHandle}
      onRenderTitle={onRenderTitle}
      options={datasources}
      required={isRequired()}
      errorMessage={error}
      className="xb-sp-input"
      styles={dropdownStyles}
    // responsiveMode={ResponsiveMode.small}
    />;

  const stackTokens: IStackTokens = {
    childrenGap: 5
  };

  return (
    <>
      <Stack horizontal verticalAlign="start" tokens={stackTokens}>
        <Stack.Item grow={1}>{input}</Stack.Item>
        {inputValue?.xml?.ReadOnly !== SharePointMetadataBooleans.true && <Stack.Item>
          {isLoading
            ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
            : <div className="xb-search-icon-container">
              <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                <FontIcon iconName="Search" onClick={openPanel} />
              </TooltipHost>
            </div>}
        </Stack.Item>}
      </Stack>
      <DataSourcePanel
        displayName={dataSourceItem?.name}
        closePanel={closePanel}
        isOpen={isOpen}
        datasourceItem={dataSourceItem}
        datasourceService={dataSourceService}
        searchInputs={searchInputs}
        updateSearchInputs={updateSearchInputs}
        updateInputValue={updateInputValue}
        boundedField={boundedField}
      />
    </>
  );
};

export const DataSourceDropdown = connect(mapStateToProps, mapDispatchToProps)(UnconnectedDataSourceDropdown);
